import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ArrowLeft as ArrowLeftIcon, Buildings as BuildingsIcon } from '@phosphor-icons/react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { paths } from '@/paths';
import { toast } from '@/components/core/v2/toaster';
import {
  getCommunity,
  getStripeConnectStatus,
  updateCommunity,
  updateCommunityPreferences,
  type UpdateCommunityData,
} from '@/api/communities';
import { useUser } from '@/hooks/v2/use-user';

// ─── constants ────────────────────────────────────────────────────────────────

const DEFAULT_RESERVATION_DESCRIPTION =
  'View all amenity reservations. Approved reservations appear in blue; your pending requests remain visible to you in orange while waiting for approval. Use the "New Reservation" button to book an amenity.';

// ─── form types ───────────────────────────────────────────────────────────────

interface DetailsFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
}

interface PreferencesFormData {
  discussion_page_enabled: boolean;
  payments_enabled: boolean;
  amenity_reservations_description: string;
}

// ─── page ─────────────────────────────────────────────────────────────────────

export function AccountCommunityPage(): React.JSX.Element {
  const { communityId } = useParams<{ communityId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { community: activeCommunity, checkSession } = useUser();

  const [editingDetails, setEditingDetails] = React.useState(false);
  const [editingPreferences, setEditingPreferences] = React.useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['community', communityId],
    queryFn: () => getCommunity(communityId!),
    enabled: !!communityId,
  });

  const community = data?.community;
  const resolvedId = community?.uuid ?? community?.ulid ?? community?.id;

  // ─── details form ──────────────────────────────────────────────────────────

  const detailsForm = useForm<DetailsFormData>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      country: '',
      zip_code: '',
    },
  });

  React.useEffect(() => {
    if (community) {
      detailsForm.reset({
        name: community.name,
        email: community.email,
        phone: community.phone,
        address: community.address,
        address2: community.address2 ?? '',
        city: community.city,
        state: community.state,
        country: community.country ?? '',
        zip_code: community.zip_code,
      });
    }
  }, [community, detailsForm.reset]);

  const updateDetailsMutation = useMutation({
    mutationFn: (data: UpdateCommunityData) =>
      updateCommunity(resolvedId!, {
        ...data,
        address2: data.address2 || null,
        country: data.country || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community', communityId] });
      queryClient.invalidateQueries({ queryKey: ['communities'] });
      toast.success('Community details updated');
      setEditingDetails(false);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'Failed to update community');
    },
  });

  const handleSaveDetails = detailsForm.handleSubmit((data) => {
    updateDetailsMutation.mutate(data);
  });

  const handleCancelDetails = () => {
    setEditingDetails(false);
    if (community) {
      detailsForm.reset({
        name: community.name,
        email: community.email,
        phone: community.phone,
        address: community.address,
        address2: community.address2 ?? '',
        city: community.city,
        state: community.state,
        country: community.country ?? '',
        zip_code: community.zip_code,
      });
    }
  };

  // ─── preferences form ──────────────────────────────────────────────────────

  const preferencesForm = useForm<PreferencesFormData>({
    defaultValues: {
      discussion_page_enabled: true,
      payments_enabled: false,
      amenity_reservations_description: '',
    },
  });

  React.useEffect(() => {
    if (community) {
      preferencesForm.reset({
        discussion_page_enabled: community.discussion_page_enabled ?? true,
        payments_enabled: community.payments_enabled ?? false,
        amenity_reservations_description: community.amenity_reservations_description ?? '',
      });
    }
  }, [community, preferencesForm.reset]);

  const refreshActiveCommunityIfNeeded = React.useCallback(async () => {
    if (activeCommunity && community && String(activeCommunity.id) === String(community.id)) {
      await checkSession();
    }
  }, [activeCommunity, checkSession, community]);

  const updatePreferencesMutation = useMutation({
    mutationFn: (data: PreferencesFormData) =>
      updateCommunityPreferences(resolvedId!, {
        discussion_page_enabled: data.discussion_page_enabled,
        payments_enabled: data.payments_enabled,
        amenity_reservations_description: data.amenity_reservations_description?.trim() || null,
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['community', communityId] });
      queryClient.invalidateQueries({ queryKey: ['communities'] });
      await refreshActiveCommunityIfNeeded();
      toast.success('Community settings updated');
      setEditingPreferences(false);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'Failed to update settings');
    },
  });

  const handleSavePreferences = preferencesForm.handleSubmit((data) => {
    updatePreferencesMutation.mutate(data);
  });

  const handleCancelPreferences = () => {
    setEditingPreferences(false);
    if (community) {
      preferencesForm.reset({
        discussion_page_enabled: community.discussion_page_enabled ?? true,
        payments_enabled: community.payments_enabled ?? false,
        amenity_reservations_description: community.amenity_reservations_description ?? '',
      });
    }
  };

  // ─── render ────────────────────────────────────────────────────────────────

  return (
    <Stack spacing={4}>
      {/* Back nav + page title */}
      <Box>
        <Button
          onClick={() => navigate(paths.settings.account.communities.index)}
          startIcon={<ArrowLeftIcon size={18} />}
          variant="text"
          size="small"
          sx={{ mb: 1 }}
        >
          Back to Communities
        </Button>
        {isLoading ? (
          <Skeleton width={240} height={32} />
        ) : community ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BuildingsIcon size={24} style={{ color: 'var(--mui-palette-primary-main)' }} />
            <Typography variant="h5" fontWeight={700}>
              {community.name}
            </Typography>
            <Chip label={community.uuid ?? community.id} size="small" variant="soft" />
          </Stack>
        ) : null}
      </Box>

      {error ? (
        <Typography color="error">Failed to load community.</Typography>
      ) : null}

      {/* ── Community Details card ─────────────────────────────────────────── */}
      <Card elevation={0} variant="outlined">
        <CardHeader
          title="Community Details"
          subheader="Name, address, and contact information."
        />
        <CardContent>
          {isLoading ? (
            <Stack spacing={2}>
              {[...Array(5)].map((_, i) => <Skeleton key={i} height={56} variant="rounded" />)}
            </Stack>
          ) : community ? (
            <form onSubmit={handleSaveDetails}>
              <Stack spacing={2}>
                <Controller
                  name="name"
                  control={detailsForm.control}
                  rules={{ required: 'Name is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth error={!!fieldState.error}>
                      <InputLabel>Name</InputLabel>
                      <OutlinedInput {...field} disabled={!editingDetails} label="Name" />
                      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Controller
                    name="email"
                    control={detailsForm.control}
                    rules={{ required: 'Email is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl sx={{ flex: 1, minWidth: 200 }} error={!!fieldState.error}>
                        <InputLabel>Email</InputLabel>
                        <OutlinedInput {...field} disabled={!editingDetails} type="email" label="Email" />
                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="phone"
                    control={detailsForm.control}
                    rules={{ required: 'Phone is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl sx={{ flex: 1, minWidth: 200 }} error={!!fieldState.error}>
                        <InputLabel>Phone</InputLabel>
                        <OutlinedInput {...field} disabled={!editingDetails} label="Phone" />
                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Box>
                <Controller
                  name="address"
                  control={detailsForm.control}
                  rules={{ required: 'Address is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth error={!!fieldState.error}>
                      <InputLabel>Address</InputLabel>
                      <OutlinedInput {...field} disabled={!editingDetails} label="Address" />
                      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
                <Controller
                  name="address2"
                  control={detailsForm.control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Address line 2</InputLabel>
                      <OutlinedInput {...field} disabled={!editingDetails} label="Address line 2" />
                    </FormControl>
                  )}
                />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Controller
                    name="city"
                    control={detailsForm.control}
                    rules={{ required: 'City is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl sx={{ flex: 1, minWidth: 140 }} error={!!fieldState.error}>
                        <InputLabel>City</InputLabel>
                        <OutlinedInput {...field} disabled={!editingDetails} label="City" />
                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="state"
                    control={detailsForm.control}
                    rules={{ required: 'State is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl sx={{ flex: 1, minWidth: 100 }} error={!!fieldState.error}>
                        <InputLabel>State</InputLabel>
                        <OutlinedInput {...field} disabled={!editingDetails} label="State" />
                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="zip_code"
                    control={detailsForm.control}
                    rules={{ required: 'ZIP Code is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl sx={{ flex: 1, minWidth: 100 }} error={!!fieldState.error}>
                        <InputLabel>ZIP Code</InputLabel>
                        <OutlinedInput {...field} disabled={!editingDetails} label="ZIP Code" />
                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="country"
                    control={detailsForm.control}
                    render={({ field }) => (
                      <FormControl sx={{ flex: 1, minWidth: 100 }}>
                        <InputLabel>Country</InputLabel>
                        <OutlinedInput {...field} disabled={!editingDetails} label="Country" />
                      </FormControl>
                    )}
                  />
                </Box>
              </Stack>
            </form>
          ) : null}
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          {!editingDetails ? (
            <Button variant="outlined" size="small" onClick={() => setEditingDetails(true)}>
              Edit
            </Button>
          ) : (
            <>
              <Button
                size="small"
                color="inherit"
                disabled={updateDetailsMutation.isPending}
                onClick={handleCancelDetails}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                loading={updateDetailsMutation.isPending}
                onClick={handleSaveDetails}
              >
                Save changes
              </LoadingButton>
            </>
          )}
        </CardActions>
      </Card>

      {/* ── Community Settings card ────────────────────────────────────────── */}
      <Card elevation={0} variant="outlined">
        <CardHeader
          title="Community Settings"
          subheader="Feature flags and content settings for this community."
        />
        <CardContent>
          {isLoading ? (
            <Stack spacing={2}>
              {[...Array(3)].map((_, i) => <Skeleton key={i} height={72} variant="rounded" />)}
            </Stack>
          ) : community ? (
            <form onSubmit={handleSavePreferences}>
              <Stack spacing={2}>
                {/* Discussion page toggle */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Discussion page
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                        A shared board where members can start topics and reply to each other.
                      </Typography>
                    </Box>
                    {editingPreferences ? (
                      <Controller
                        name="discussion_page_enabled"
                        control={preferencesForm.control}
                        render={({ field: { value, onChange, ...field } }) => (
                          <Switch
                            {...field}
                            checked={value ?? true}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        )}
                      />
                    ) : (
                      <Chip
                        label={community.discussion_page_enabled ? 'Enabled' : 'Disabled'}
                        size="small"
                        variant="soft"
                        color={community.discussion_page_enabled ? 'success' : 'default'}
                      />
                    )}
                  </Box>
                </Box>

                {/* Payments toggle */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Payments
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                        Allows this community to configure Stripe Connect and collect reservation deposits.
                        Enable only after the community has agreed to payment terms.
                      </Typography>
                    </Box>
                    {editingPreferences ? (
                      <Controller
                        name="payments_enabled"
                        control={preferencesForm.control}
                        render={({ field: { value, onChange, ...field } }) => (
                          <Switch
                            {...field}
                            checked={value ?? false}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        )}
                      />
                    ) : (
                      <Chip
                        label={community.payments_enabled ? 'Enabled' : 'Disabled'}
                        size="small"
                        variant="soft"
                        color={community.payments_enabled ? 'success' : 'default'}
                      />
                    )}
                  </Box>
                </Box>

                {/* Reservation page description */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Reservation page description
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
                    Text shown at the top of the amenity reservations page. Leave blank for the default message.
                  </Typography>
                  <Controller
                    name="amenity_reservations_description"
                    control={preferencesForm.control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={!editingPreferences}
                        placeholder={DEFAULT_RESERVATION_DESCRIPTION}
                        label="Description"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </form>
          ) : null}
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          {!editingPreferences ? (
            <Button variant="outlined" size="small" onClick={() => setEditingPreferences(true)}>
              Edit
            </Button>
          ) : (
            <>
              <Button
                size="small"
                color="inherit"
                disabled={updatePreferencesMutation.isPending}
                onClick={handleCancelPreferences}
              >
                Cancel
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                loading={updatePreferencesMutation.isPending}
                onClick={handleSavePreferences}
              >
                Save changes
              </LoadingButton>
            </>
          )}
        </CardActions>
      </Card>

      {/* ── Stripe Connect info card ──────────────────────────────────────── */}
      {community && <StripeAccountCard communityId={community.id} />}
    </Stack>
  );
}

// ─── Stripe account overview ────────────────────────────────────────────────

function StripeAccountCard({ communityId }: { communityId: string }): React.JSX.Element | null {
  const { data: status, isLoading } = useQuery({
    queryKey: ['stripe-connect-status', communityId],
    queryFn: () => getStripeConnectStatus(communityId),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <Card elevation={0} variant="outlined">
        <CardHeader title="Stripe Account" />
        <CardContent>
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={24} width={180} />
            <Skeleton variant="rounded" height={20} width={280} />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (!status?.connected) {
    return (
      <Card elevation={0} variant="outlined">
        <CardHeader title="Stripe Account" />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No Stripe account connected. The community manager can connect one from Community Settings → Payments.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const isActive = status.charges_enabled && status.onboarding_complete;

  return (
    <Card elevation={0} variant="outlined">
      <CardHeader
        title="Stripe Account"
        action={
          <Chip
            label={isActive ? 'Active' : status.onboarding_complete ? 'Pending review' : 'Setup incomplete'}
            size="small"
            color={isActive ? 'success' : status.onboarding_complete ? 'info' : 'warning'}
          />
        }
      />
      <CardContent>
        <Stack spacing={1}>
          {status.business_name && (
            <Typography variant="body2" color="text.secondary">
              Business: <strong>{status.business_name}</strong>
            </Typography>
          )}
          {status.email && (
            <Typography variant="body2" color="text.secondary">
              Email: <strong>{status.email}</strong>
            </Typography>
          )}
          {status.bank_last4 && (
            <Typography variant="body2" color="text.secondary">
              Bank account: <strong>{status.bank_name ? `${status.bank_name} ` : ''}····{status.bank_last4}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Account ID: <strong>{status.account_id}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Charges: <strong>{status.charges_enabled ? 'Enabled' : 'Disabled'}</strong> · Payouts: <strong>{status.payouts_enabled ? 'Enabled' : 'Disabled'}</strong>
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
